import type { LucideIcon } from 'lucide-react';
import { Loader2, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  /** Leading icon in empty mode; defaults to `Search`. */
  icon?: LucideIcon;
  /** Loading variant: spinner + loading copy. Defaults to `false`. */
  loading?: boolean;
  /** Empty-mode title; generic default copy — no feature-specific text baked in. */
  title?: string;
  description?: string;
  loadingTitle?: string;
  loadingDescription?: string;
}

/**
 * Generic empty/loading state for any screen. Screens that need specific
 * copy (e.g. the investigator) pass it explicitly.
 */
export function EmptyState({
  icon: Icon = Search,
  loading = false,
  title,
  description,
  loadingTitle,
  loadingDescription,
}: EmptyStateProps) {
  const displayTitle = loading ? (loadingTitle ?? 'Loading') : (title ?? 'Nothing here');
  const displayDescription = loading
    ? (loadingDescription ?? 'Fetching data. This should only take a moment.')
    : (description ?? "There's nothing to show in this view yet.");

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-100 text-center px-8">
      <div
        className={cn(
          'w-16 h-16 rounded-full border flex items-center justify-center mb-4',
          loading ? 'bg-success/10 border-success/30' : 'bg-card border-border',
        )}
      >
        {loading ? (
          <Loader2 className="w-7 h-7 text-success animate-spin" aria-hidden />
        ) : (
          <Icon className="w-7 h-7 text-muted-foreground" aria-hidden />
        )}
      </div>
      <h2 className="text-lg font-medium text-foreground mb-2">{displayTitle}</h2>
      <p className="text-sm text-muted-foreground max-w-md leading-relaxed">{displayDescription}</p>
    </div>
  );
}
